import { useState, useEffect } from 'react';
import { X, Loader2, RefreshCw, User } from 'lucide-react';
import api from '@/lib/api';
import { formatDateTime } from '@/lib/dateFormat';

interface Props {
  leadId: string;
  onClose: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name', firstName: 'First Name', lastName: 'Last Name',
  status: 'Status', source: 'Source', priority: 'Priority',
  phones: 'Phones', emails: 'Emails',
  address: 'Address', country: 'Country', state: 'State', city: 'City', pincode: 'Zipcode',
  notes: 'Notes/Description', campaignName: 'Campaign',
  leadCategory: 'Category', leadColor: 'Color',
  assignedToId: 'Assigned To',
  utmSource: 'UTM Source', utmMedium: 'UTM Medium', utmCampaign: 'UTM Campaign',
  utmTerm: 'UTM Term', utmContent: 'UTM Content',
  hashtags: 'Hashtags', score: 'Score',
};

function formatValue(val: any): string {
  if (val === null || val === undefined || val === '') return '—';
  if (Array.isArray(val)) {
    if (val.length === 0) return '—';
    // Handle phone/email arrays
    if (typeof val[0] === 'object') return val.map((v: any) => v.number || v.email || JSON.stringify(v)).join(', ');
    return val.join(', ');
  }
  return String(val);
}

export function LeadLogsModal({ leadId, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState<any>(null);
  const [activities, setActivities] = useState<any[]>([]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${leadId}/logs`);
      setLead(data.lead);
      setActivities(data.activities ?? []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, [leadId]);

  // Count unique fields changed across all activities
  const fieldsChanged = new Set<string>();
  const lastUpdatedBy = activities[0]?.actor?.name ?? '—';
  activities.forEach(a => {
    if (a.payload?.changes) Object.keys(a.payload.changes).forEach(k => fieldsChanged.add(k));
  });

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Lead Logs</h2>
            <p className="text-sm text-slate-500">{lead?.name} · Lead ID: {leadId.slice(-6)}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchLogs} className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <StatCard label="Total Records" value={activities.length} />
              <StatCard label="Fields Changed" value={fieldsChanged.size} />
              <StatCard label="Last Updated By" value={lastUpdatedBy} />
            </div>

            {/* Activity list */}
            <div className="space-y-4">
              {activities.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-8">No logs found</p>
              ) : activities.map((activity, idx) => (
                <LogEntry key={activity.id} activity={activity} index={activities.length - idx} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="border border-slate-100 rounded-xl px-4 py-3">
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-xl font-bold text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}

function LogEntry({ activity, index }: { activity: any; index: number }) {
  const changes = activity.payload?.changes as Record<string, { from: any; to: any }> | undefined;
  const hasFieldChanges = changes && Object.keys(changes).length > 0;

  const actionLabel = activity.actionType.replace(/_/g, ' ');
  const actionColor = activity.actionType === 'lead_created' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : activity.actionType === 'lead_status_changed' ? 'bg-amber-50 text-amber-700 border-amber-200'
    : activity.actionType === 'followup_added' ? 'bg-purple-50 text-purple-700 border-purple-200'
    : activity.actionType === 'ASSIGNED' ? 'bg-blue-50 text-blue-700 border-blue-200'
    : 'bg-slate-50 text-slate-600 border-slate-200';

  return (
    <div className="rounded-xl border border-slate-100 bg-white overflow-hidden">
      {/* Entry header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50/60 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase border ${actionColor}`}>
            {actionLabel}
          </span>
          <span className="text-xs text-slate-400">#{index}</span>
        </div>
        <span className="text-xs text-slate-400">{formatDateTime(activity.createdAt)}</span>
      </div>

      {/* Field changes */}
      {hasFieldChanges && (
        <div className="px-4 py-3 space-y-2.5">
          {Object.entries(changes!).map(([field, { from, to }]) => (
            <div key={field} className="grid grid-cols-[1fr_1fr] gap-3">
              <div className="rounded-lg bg-red-50/60 border border-red-100 px-3 py-2">
                <p className="text-[10px] font-semibold text-red-500 uppercase mb-0.5">
                  {FIELD_LABELS[field] || field} (Old)
                </p>
                <p className="text-sm text-slate-700 font-mono break-all">{formatValue(from)}</p>
              </div>
              <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 px-3 py-2">
                <p className="text-[10px] font-semibold text-emerald-600 uppercase mb-0.5">
                  {FIELD_LABELS[field] || field} (New)
                </p>
                <p className="text-sm text-slate-700 font-mono break-all">{formatValue(to)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Non-change payloads (created, assigned, followup, note) */}
      {!hasFieldChanges && activity.payload && (
        <div className="px-4 py-3">
          {activity.payload.message && (
            <p className="text-sm text-slate-600">{activity.payload.message}</p>
          )}
          {activity.payload.remark && (
            <p className="text-sm text-slate-600">{activity.payload.remark}</p>
          )}
          {activity.actionType === 'lead_created' && (
            <p className="text-sm text-slate-500 italic">Lead was created</p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center gap-4 px-4 py-2.5 border-t border-slate-50 bg-slate-50/30 text-xs text-slate-400">
        <span className="flex items-center gap-1">
          <User className="h-3 w-3" /> {activity.actor?.name ?? '—'}
        </span>
        <span>{formatDateTime(activity.createdAt)}</span>
      </div>
    </div>
  );
}
